import {
    Charset,
    DEFAULT_CHARSET,
    charsetFromDeclaration,
    decodeDefaultRepertoire,
    decodeText,
} from './charset';
import { vrForTag } from './dictionary';
import { SPECIFIC_CHARACTER_SET_TAG, Tag, formatTag } from './tag';
import {
    Vr,
    binaryValueWidth,
    isByteStreamVr,
    isCharacterSetAware,
    isMultiValued,
    isStringVr,
    paddingByte,
} from './vr';

/**
 * One item of a sequence: a nested dataset plus the length framing it arrived
 * with.
 *
 * PS3.5 permits an item to declare its length or to be delimited, and both
 * are valid for the same content. Recording which was used lets an unedited
 * dataset be written back byte-for-byte, which is what makes a serialized
 * result checkable by comparison rather than by inspection.
 */
export interface DicomItem {
    readonly dataset: DicomDataset;
    readonly undefinedLength: boolean;
}

/** Encapsulated (compressed) pixel data: a basic offset table and its frames. */
export interface PixelDataFragments {
    /** The basic offset table's bytes, empty when the table is absent. */
    readonly offsetTable: Uint8Array;
    /** One entry per fragment, each a view over the source buffer. */
    readonly fragments: readonly Uint8Array[];
}

type ElementContent =
    | { readonly kind: 'strings'; readonly values: readonly string[] }
    | { readonly kind: 'numbers'; readonly values: readonly number[] }
    | { readonly kind: 'bigints'; readonly values: readonly bigint[] }
    | { readonly kind: 'bytes'; readonly bytes: Uint8Array }
    | {
          readonly kind: 'sequence';
          readonly items: readonly DicomItem[];
          readonly undefinedLength: boolean;
      }
    | { readonly kind: 'fragments'; readonly value: PixelDataFragments };

/**
 * One DICOM attribute.
 *
 * An element parsed from a file keeps a view over its own value bytes and
 * decodes them only when a caller asks for the value — the eager work is the
 * tag/offset index, not the content. The source view is retained after
 * decoding so that writing an attribute nobody edited reproduces its exact
 * bytes; a setter drops it, because from then on the bytes must be derived
 * from the value rather than the other way round.
 */
export class DicomElement {
    readonly tag: Tag;
    readonly vr: Vr;

    /**
     * The value representation the stream actually carried, where it differs
     * from the one used to decode.
     *
     * They differ for a value too long for a 16-bit length field: PS3.5 gives
     * most VRs a short length, so a value of 65,536 bytes or more is written
     * as UN with the 32-bit form, and its real representation comes from the
     * dictionary. Keeping what was on the wire is what lets an element decoded
     * as a decimal string be written back as the UN it arrived as, so a file
     * nobody edited still reproduces exactly.
     */
    private readonly onWireVr: Vr;

    /**
     * The element's value field exactly as it appeared in the source buffer,
     * or undefined once the value has been set from outside.
     */
    private sourceBytes: Uint8Array | undefined;
    private content: ElementContent | undefined;
    private charset: Charset;

    private constructor(
        tag: Tag,
        vr: Vr,
        sourceBytes: Uint8Array | undefined,
        content: ElementContent | undefined,
        charset: Charset,
        onWireVr: Vr = vr
    ) {
        this.tag = tag;
        this.vr = vr;
        this.onWireVr = onWireVr;
        this.sourceBytes = sourceBytes;
        this.content = content;
        this.charset = charset;
    }

    /**
     * Builds an element that defers decoding of `bytes` until first access.
     *
     * `onWireVr` records what the stream carried when that is not the VR the
     * value is decoded with; see {@link DicomElement.onWireVr}.
     */
    static fromSource(
        tag: Tag,
        vr: Vr,
        bytes: Uint8Array,
        charset: Charset,
        onWireVr: Vr = vr
    ): DicomElement {
        return new DicomElement(tag, vr, bytes, undefined, charset, onWireVr);
    }

    /**
     * Builds a sequence element from already-parsed items.
     *
     * `vr` is normally SQ, and is UN for a sequence that reached us as an
     * undefined-length UN element (PS3.5 section 7.5). That distinction is
     * kept rather than normalized away: a UN sequence's items are
     * implicit-VR encoded whatever the enclosing syntax, so the writer needs
     * to know which of the two it is holding.
     */
    static fromItems(
        tag: Tag,
        items: readonly DicomItem[],
        undefinedLength: boolean,
        vr: Vr = 'SQ',
        onWireVr: Vr = vr
    ): DicomElement {
        return new DicomElement(
            tag,
            vr,
            undefined,
            { kind: 'sequence', items, undefinedLength },
            DEFAULT_CHARSET,
            onWireVr
        );
    }

    /** Builds an encapsulated PixelData element from its parsed fragments. */
    static fromFragments(
        tag: Tag,
        vr: Vr,
        value: PixelDataFragments
    ): DicomElement {
        return new DicomElement(
            tag,
            vr,
            undefined,
            { kind: 'fragments', value },
            DEFAULT_CHARSET
        );
    }

    static fromStrings(
        tag: Tag,
        vr: Vr,
        values: readonly string[],
        charset: Charset = DEFAULT_CHARSET
    ): DicomElement {
        return new DicomElement(
            tag,
            vr,
            undefined,
            { kind: 'strings', values: [...values] },
            charset
        );
    }

    static fromNumbers(
        tag: Tag,
        vr: Vr,
        values: readonly number[]
    ): DicomElement {
        return new DicomElement(
            tag,
            vr,
            undefined,
            { kind: 'numbers', values: [...values] },
            DEFAULT_CHARSET
        );
    }

    static fromBytes(tag: Tag, vr: Vr, bytes: Uint8Array): DicomElement {
        return new DicomElement(
            tag,
            vr,
            undefined,
            { kind: 'bytes', bytes },
            DEFAULT_CHARSET
        );
    }

    /**
     * The element's source bytes, or undefined if it has no unedited source.
     *
     * The writer emits these verbatim when present, which is what keeps an
     * unedited attribute byte-identical across a read/write cycle.
     */
    get unmodifiedBytes(): Uint8Array | undefined {
        return this.sourceBytes;
    }

    /**
     * The value representation to write this element under.
     *
     * An unedited element still holds its source bytes and is written back as
     * it arrived, so it keeps the representation the stream carried. Once a
     * setter has replaced the value there are no original bytes to preserve,
     * and the writer encodes under the real VR instead.
     */
    get vrForWrite(): Vr {
        // A sequence keeps the representation it arrived under whatever its
        // items have been edited to. A UN sequence holds implicit-VR content
        // by definition, so rewriting it as SQ would change how every item in
        // it is encoded, not just the two bytes naming the sequence.
        if (this.content?.kind === 'sequence') {
            return this.onWireVr;
        }
        return this.sourceBytes !== undefined ? this.onWireVr : this.vr;
    }

    /**
     * The character set this element's text is encoded in — the one it was
     * read under, or the one in force when its value was set.
     *
     * Carried on the element rather than looked up from the dataset at write
     * time so that an element moved between datasets cannot be re-encoded
     * under a character set it was never written in.
     */
    get charsetForWrite(): Charset {
        return this.charset;
    }

    /** The decoded value, computed on first access and cached. */
    get value(): ElementContent {
        if (this.content === undefined) {
            this.content = this.decode();
        }
        return this.content;
    }

    private decode(): ElementContent {
        const bytes = this.sourceBytes ?? new Uint8Array(0);
        if (this.vr === 'SQ') {
            // A sequence always arrives via fromItems; reaching here means an
            // SQ whose items were never parsed, which is an empty sequence.
            return { kind: 'sequence', items: [], undefinedLength: false };
        }
        if (isByteStreamVr(this.vr)) {
            // A UN element carrying a sequence arrives via fromItems too, so
            // anything still undecoded under a byte-stream VR is opaque data.
            return { kind: 'bytes', bytes };
        }
        if (isStringVr(this.vr)) {
            return { kind: 'strings', values: this.decodeStrings(bytes) };
        }
        if (this.vr === 'SV' || this.vr === 'UV') {
            return { kind: 'bigints', values: decodeBigInts(bytes, this.vr) };
        }
        return { kind: 'numbers', values: decodeNumbers(bytes, this.vr) };
    }

    private decodeStrings(bytes: Uint8Array): string[] {
        const text = isCharacterSetAware(this.vr)
            ? decodeText(bytes, this.charset)
            : decodeDefaultRepertoire(bytes);
        const unpadded = trimPadding(text, paddingByte(this.vr));
        if (!isMultiValued(this.vr)) {
            return unpadded.length === 0 && bytes.length === 0
                ? []
                : [unpadded];
        }
        if (unpadded.length === 0) {
            return [];
        }
        // Only the padding of the value as a whole is stripped above; each
        // component keeps its own significant content but sheds the leading
        // and trailing space the standard permits around it.
        return unpadded.split('\\').map((part) => part.trim());
    }

    /** Replaces the value with strings, discarding the source bytes. */
    setStrings(values: readonly string[], charset: Charset): void {
        this.sourceBytes = undefined;
        this.charset = charset;
        this.content = { kind: 'strings', values: [...values] };
    }

    /** Replaces the value with numbers, discarding the source bytes. */
    setNumbers(values: readonly number[]): void {
        this.sourceBytes = undefined;
        this.content = { kind: 'numbers', values: [...values] };
    }

    /** Replaces the value with raw bytes, discarding the source bytes. */
    setBytes(bytes: Uint8Array): void {
        this.sourceBytes = undefined;
        this.content = { kind: 'bytes', bytes };
    }

    /** Replaces the sequence's items, discarding the source bytes. */
    setItems(items: readonly DicomItem[]): void {
        this.sourceBytes = undefined;
        const previous = this.content;
        this.content = {
            kind: 'sequence',
            items: [...items],
            undefinedLength:
                previous?.kind === 'sequence'
                    ? previous.undefinedLength
                    : false,
        };
    }
}

/** Strips the VR's padding byte from the end of a decoded string. */
function trimPadding(text: string, padding: number): string {
    let end = text.length;
    while (
        end > 0 &&
        (text.charCodeAt(end - 1) === padding || text.charCodeAt(end - 1) === 0)
    ) {
        end--;
    }
    return text.slice(0, end);
}

/**
 * Reads a fixed-width binary value field as numbers.
 *
 * The bytes are read through a DataView rather than a typed-array view: a
 * value field is only guaranteed to be 2-byte aligned within the file, so a
 * `new Uint32Array(buffer, offset, n)` over a 4-byte VR throws on an odd
 * multiple of 2. DataView has no alignment requirement.
 */
function decodeNumbers(bytes: Uint8Array, vr: Vr): number[] {
    const width = binaryValueWidth(vr);
    if (width === undefined || bytes.length < width) {
        return [];
    }
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const count = Math.floor(bytes.length / width);
    const values: number[] = new Array<number>(count);
    for (let i = 0; i < count; i++) {
        const at = i * width;
        switch (vr) {
            case 'FL':
                values[i] = view.getFloat32(at, true);
                break;
            case 'FD':
                values[i] = view.getFloat64(at, true);
                break;
            case 'SL':
                values[i] = view.getInt32(at, true);
                break;
            case 'SS':
                values[i] = view.getInt16(at, true);
                break;
            case 'UL':
                values[i] = view.getUint32(at, true);
                break;
            case 'US':
                values[i] = view.getUint16(at, true);
                break;
            case 'AT':
                // An AT value is a tag: group then element, each little-endian.
                values[i] =
                    ((view.getUint16(at, true) << 16) |
                        view.getUint16(at + 2, true)) >>>
                    0;
                break;
            default:
                values[i] = 0;
                break;
        }
    }
    return values;
}

/** Reads a 64-bit integer value field. SV and UV exceed Number's exact range. */
function decodeBigInts(bytes: Uint8Array, vr: Vr): bigint[] {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const count = Math.floor(bytes.length / 8);
    const values: bigint[] = new Array<bigint>(count);
    for (let i = 0; i < count; i++) {
        values[i] =
            vr === 'SV'
                ? view.getBigInt64(i * 8, true)
                : view.getBigUint64(i * 8, true);
    }
    return values;
}

/**
 * A DICOM dataset: attributes keyed by tag, with the character set that
 * governs decoding its text.
 *
 * The model differs from dcmjs's naturalized form in the three ways that
 * caused defects there, and the differences are the point:
 *
 * - a sequence is **always** an array of items, whatever its length;
 * - a value's multiplicity is chosen by the accessor a caller reaches for,
 *   not guessed from whether the parser happened to produce an array;
 * - nothing is wrapped in a Proxy or a boxed String, so a value read out is
 *   an ordinary JavaScript value that behaves like one.
 */
export class DicomDataset {
    private readonly elements = new Map<Tag, DicomElement>();
    private charset: Charset = DEFAULT_CHARSET;

    /** The character set governing this dataset's character-set-aware VRs. */
    get specificCharset(): Charset {
        return this.charset;
    }

    /**
     * Sets the charset used to decode subsequently-read text.
     *
     * The parser calls this the moment it reads (0008,0005), which precedes
     * every character-set-aware attribute because elements are ordered by
     * tag and (0008,0005) sorts before them all.
     */
    setCharset(charset: Charset): void {
        this.charset = charset;
    }

    get size(): number {
        return this.elements.size;
    }

    /** The dataset's tags, in ascending order — the order PS3.5 requires on write. */
    tags(): Tag[] {
        return [...this.elements.keys()].sort((a, b) => a - b);
    }

    /** The dataset's elements, in ascending tag order. */
    orderedElements(): DicomElement[] {
        return this.tags().map((tag) => {
            const element = this.elements.get(tag);
            /* istanbul ignore next -- keys() and get() cannot disagree */
            if (!element) {
                throw new Error(
                    `Element ${formatTag(tag)} vanished from dataset`
                );
            }
            return element;
        });
    }

    has(tag: Tag): boolean {
        return this.elements.has(tag);
    }

    element(tag: Tag): DicomElement | undefined {
        return this.elements.get(tag);
    }

    /** Adds or replaces an element, keeping the charset in step with (0008,0005). */
    put(element: DicomElement): void {
        this.elements.set(element.tag, element);
        if (element.tag === SPECIFIC_CHARACTER_SET_TAG) {
            const declared = this.rawStrings(element).join('\\');
            this.charset = charsetFromDeclaration(declared);
        }
    }

    delete(tag: Tag): boolean {
        return this.elements.delete(tag);
    }

    private rawStrings(element: DicomElement): readonly string[] {
        const content = element.value;
        return content.kind === 'strings' ? content.values : [];
    }

    /**
     * All string values of an attribute, always as an array.
     *
     * Numeric VRs are rendered as strings so that a caller displaying an
     * attribute does not have to know its VR; use `numbers` when the value
     * is to be computed with.
     */
    strings(tag: Tag): readonly string[] {
        const element = this.elements.get(tag);
        if (!element) {
            return [];
        }
        const content = element.value;
        switch (content.kind) {
            case 'strings':
                return content.values;
            case 'numbers':
                return content.values.map((value) => String(value));
            case 'bigints':
                return content.values.map((value) => value.toString());
            default:
                return [];
        }
    }

    /**
     * The single string value of an attribute, or undefined when absent or
     * empty.
     *
     * An attribute present but zero-length (DICOM type 2) is empty, not
     * missing; both answer undefined here because neither carries a value,
     * and `has` distinguishes them when the difference matters.
     */
    string(tag: Tag): string | undefined {
        return this.strings(tag)[0];
    }

    /** All numeric values of an attribute, always as an array. */
    numbers(tag: Tag): readonly number[] {
        const element = this.elements.get(tag);
        if (!element) {
            return [];
        }
        const content = element.value;
        switch (content.kind) {
            case 'numbers':
                return content.values;
            case 'strings':
                // DS and IS carry numbers as text. A component that is not a
                // number yields NaN rather than 0: a silently substituted
                // zero is a clinical value invented by the parser.
                //
                // An empty component has to be spelled out, because
                // `Number('')` is 0 rather than NaN. A position of
                // `\-250\-100` would otherwise read as a complete coordinate
                // triple sitting on the origin, and nothing downstream could
                // tell that from a slice genuinely positioned there.
                return content.values.map((value) => {
                    const trimmed = value.trim();
                    return trimmed === '' ? NaN : Number(trimmed);
                });
            case 'bigints':
                return content.values.map((value) => Number(value));
            default:
                return [];
        }
    }

    /** The single numeric value of an attribute, or undefined when absent. */
    number(tag: Tag): number | undefined {
        return this.numbers(tag)[0];
    }

    /**
     * A sequence's items — **always** an array, empty when the attribute is
     * absent or holds no items.
     *
     * There is no single-item special case. The absence of one is the reason
     * this model exists.
     */
    items(tag: Tag): readonly DicomItem[] {
        const element = this.elements.get(tag);
        if (!element) {
            return [];
        }
        const content = element.value;
        return content.kind === 'sequence' ? content.items : [];
    }

    /** A sequence's item datasets, always an array. */
    sequence(tag: Tag): DicomDataset[] {
        return this.items(tag).map((item) => item.dataset);
    }

    /** An attribute's raw bytes for the byte-stream VRs, or undefined. */
    bytes(tag: Tag): Uint8Array | undefined {
        const element = this.elements.get(tag);
        if (!element) {
            return undefined;
        }
        const content = element.value;
        return content.kind === 'bytes' ? content.bytes : undefined;
    }

    /** Encapsulated PixelData fragments, or undefined for native pixel data. */
    fragments(tag: Tag): PixelDataFragments | undefined {
        const element = this.elements.get(tag);
        if (!element) {
            return undefined;
        }
        const content = element.value;
        return content.kind === 'fragments' ? content.value : undefined;
    }

    /** Sets a string-valued attribute, using the dataset's own character set. */
    setStrings(tag: Tag, values: readonly string[], vr?: Vr): void {
        const resolved = vr ?? this.elements.get(tag)?.vr ?? vrForTag(tag);
        const element = DicomElement.fromStrings(
            tag,
            resolved,
            values,
            this.charset
        );
        this.put(element);
    }

    setString(tag: Tag, value: string, vr?: Vr): void {
        this.setStrings(tag, [value], vr);
    }

    /** Sets a numeric attribute. */
    setNumbers(tag: Tag, values: readonly number[], vr?: Vr): void {
        const resolved = vr ?? this.elements.get(tag)?.vr ?? vrForTag(tag);
        this.put(DicomElement.fromNumbers(tag, resolved, values));
    }

    setNumber(tag: Tag, value: number, vr?: Vr): void {
        this.setNumbers(tag, [value], vr);
    }

    /**
     * Sets a sequence attribute from item datasets.
     *
     * An attribute that is already present keeps the length framing it
     * arrived with, so editing one ROI of a structure set does not re-frame
     * every sequence in it — the framing is the file's, not this writer's, and
     * changing it turns a one-attribute edit into a diff against the whole
     * object. A new attribute is framed with defined lengths.
     */
    setSequence(tag: Tag, datasets: readonly DicomDataset[]): void {
        const existing = this.elements.get(tag)?.value;
        const undefinedLength =
            existing?.kind === 'sequence' ? existing.undefinedLength : false;
        const items = datasets.map((dataset) => ({
            dataset,
            undefinedLength,
        }));
        this.put(DicomElement.fromItems(tag, items, undefinedLength));
    }
}
